import {useEffect, useRef, useState} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Text} from "@pg-design/text-module";
import {
    getTinderDeckOfferCounterLabel,
    HomematchPropertyCardDetail,
    HomematchThumbDirection,
    HomematchTinderActionBar,
    HomematchTinderDeck,
    HomematchTinderDetailsCard,
    HomematchTinderListingModal,
    useHomematchTinderDeckAnimation
} from "@pg-rp/homematch-tinder";

import {addNotification} from "../../notifications/state/notifications_slice";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch} from "../../store/hooks";
import {useGetHomematchPropertyQuery} from "../api/get_homematch_property";
import {useGetHomematchVariablesQuery} from "../api/get_homematch_variables";
import {useHomematchTinderDeck} from "../hooks/use_homematch_tinder_deck";
import {
    trackHomematchTinderClose,
    trackHomematchTinderMoreInfo,
    trackHomematchTinderRate,
    trackHomematchTinderSeeAll,
    trackHomematchTinderViewItem
} from "../tracking/track_homematch_tinder";
import {HomematchTinderPropertyMap} from "./HomematchTinderPropertyMap";

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

const TINDER_MODAL_OVERLAY_Z_INDEX = 9000;
const SENT_LEAD_MESSAGE_VISIBILITY_MS = 3500;

export const HomematchTinderModal = (props: IProps) => {
    const dispatch = useAppDispatch();
    const deck = useHomematchTinderDeck({isOpen: props.isOpen});
    /** Cards swiped since the modal opened — drives the counter and the advance animation. */
    const [sessionSwipes, setSessionSwipes] = useState(0);
    const [sentLeadVendorName, setSentLeadVendorName] = useState<string | null>(null);
    const sentLeadTimerRef = useRef<number>(undefined);
    const viewedPreleadIdsRef = useRef(new Set<number>());

    useEffect(() => () => window.clearTimeout(sentLeadTimerRef.current), []);

    const currentPrelead = deck.currentPrelead;
    const propertyRpId = currentPrelead?.property.rp_id ?? null;
    const isDeckEmpty = !deck.isLoading && currentPrelead === null;

    const {
        currentData: property,
        isError: isPropertyError,
        isFetching,
        isLoading,
        isUninitialized
    } = useGetHomematchPropertyQuery({propertyRpId: propertyRpId ?? 0}, {skip: propertyRpId === null, refetchOnMountOrArgChange: true});
    const {data: variables} = useGetHomematchVariablesQuery(undefined, {skip: !props.isOpen});

    const isPropertyLoading = isLoading || isUninitialized || isFetching;

    const animation = useHomematchTinderDeckAnimation({
        activePreleadId: currentPrelead?.id,
        currentPreleadIndex: sessionSwipes,
        isOpen: props.isOpen
    });

    // The outgoing card keeps its content while the advance animation runs.
    const lastPropertyRef = useRef(property);

    useEffect(() => {
        if (!animation.isAdvancing) {
            lastPropertyRef.current = property;
        }
    }, [animation.isAdvancing, property]);

    const displayedProperty = animation.isAdvancing ? lastPropertyRef.current : property;
    const currentPreleadId = currentPrelead?.id;

    useEffect(() => {
        if (currentPreleadId === undefined) {
            return;
        }

        animation.handleCardReady(currentPreleadId, isPropertyError || (!isPropertyLoading && Boolean(property)));
    }, [animation.handleCardReady, currentPreleadId, isPropertyError, isPropertyLoading, property]);

    useEffect(() => {
        if (!props.isOpen) {
            viewedPreleadIdsRef.current.clear();
            setSessionSwipes(0);
        }
    }, [props.isOpen]);

    useEffect(() => {
        if (
            !props.isOpen ||
            animation.isAdvancing ||
            isPropertyLoading ||
            deck.leadRequirementsError ||
            !property ||
            property.id !== propertyRpId ||
            currentPreleadId === undefined ||
            viewedPreleadIdsRef.current.has(currentPreleadId)
        ) {
            return;
        }

        trackHomematchTinderViewItem(property, viewedPreleadIdsRef.current.size);
        viewedPreleadIdsRef.current.add(currentPreleadId);
    }, [animation.isAdvancing, currentPreleadId, deck.leadRequirementsError, isPropertyLoading, property, propertyRpId, props.isOpen]);

    const isRatingBlocked = !property || property.id !== propertyRpId || isPropertyLoading || deck.isSwiping || animation.isAdvancing;

    const acceptCurrent = async () => {
        const prelead = currentPrelead;

        if (!prelead || !property || isRatingBlocked) {
            return;
        }

        const result = await deck.acceptCurrentPrelead();

        if (result === "success") {
            trackHomematchTinderRate(property, "like");
            setSessionSwipes((swipes) => swipes + 1);
            setSentLeadVendorName(prelead.property.offer?.vendor?.name ?? null);
            window.clearTimeout(sentLeadTimerRef.current);
            sentLeadTimerRef.current = window.setTimeout(() => setSentLeadVendorName(null), SENT_LEAD_MESSAGE_VISIBILITY_MS);
        } else if (result === "error") {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
        }
    };

    const rejectCurrent = async () => {
        const prelead = currentPrelead;

        if (!prelead || !property || isRatingBlocked) {
            return;
        }

        const result = await deck.rejectCurrentPrelead({reasons: []});

        if (result === "success") {
            trackHomematchTinderRate(property, "dislike");
            setSessionSwipes((swipes) => swipes + 1);
        } else if (result === "error") {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
        }
    };

    const handleThumbClick = (direction: HomematchThumbDirection) => {
        if (direction === HomematchThumbDirection.Up) {
            acceptCurrent();
        } else {
            rejectCurrent();
        }
    };

    const isShellLoading = sessionSwipes === 0 && !property && (deck.isLoading || isPropertyLoading) && !isDeckEmpty;
    const showDeckShadows = deck.shadowPreleadIds.length > 0 || animation.isAdvancing;

    const offerCounterLabel = currentPrelead
        ? getTinderDeckOfferCounterLabel({
              preleadId: currentPrelead.id,
              remainingPreleadsList: deck.queue,
              trackingIndex: sessionSwipes
          })
        : undefined;

    const dictionaries = variables
        ? {
              facilitiesInfo: variables.offer_facilities,
              parkingPlacesTypes: variables.offer_parking_place_type,
              quarterTypes: variables.property_quarters,
              standardDescriptions: variables.offer_standard_description_type
          }
        : undefined;

    return (
        <HomematchTinderListingModal
            className={modalWidthStyle}
            handleClose={props.onClose}
            isLoading={isShellLoading}
            isOpen={props.isOpen}
            offerCounterLabel={offerCounterLabel}
            onCloseButtonClick={() => trackHomematchTinderClose("close_x")}
            onCloseOutsideClick={() => trackHomematchTinderClose("close_outside")}
            overlayZIndex={TINDER_MODAL_OVERLAY_Z_INDEX}
        >
            {deck.leadRequirementsError ? (
                <div className={statePanelStyle} role="alert">
                    <Text as="p" variant="body_copy_1" className={stateTextStyle}>
                        {deck.leadRequirementsError.detail}
                    </Text>
                    <Button variant="filled_primary" size="medium" onClick={deck.clearLeadRequirementsError}>
                        Wróć do przeglądania
                    </Button>
                </div>
            ) : isDeckEmpty ? (
                <div className={statePanelStyle} role="status">
                    <Text as="p" variant="body_copy_1" className={stateTextStyle}>
                        To wszystko na teraz — przygotowujemy kolejne dopasowania, wróć do nas później.
                    </Text>
                    <Button variant="filled_primary" size="medium" onClick={props.onClose}>
                        Zamknij
                    </Button>
                </div>
            ) : (
                <HomematchTinderDeck
                    frozenHeight={animation.frozenHeight}
                    isAdvancing={animation.isAdvancing}
                    setDeckNode={animation.setDeckNode}
                    shadowKeys={deck.shadowPreleadIds}
                    shouldReduceMotion={animation.shouldReduceMotion}
                    showDeckShadows={showDeckShadows}
                >
                    <HomematchTinderDetailsCard
                        actionBar={
                            <HomematchTinderActionBar
                                disabled={isRatingBlocked}
                                onThumbClick={handleThumbClick}
                                vendorLogoUrl={currentPrelead?.property.offer?.vendor?.logo_thumbnails?.v_log_100}
                                vendorName={currentPrelead?.property.offer?.vendor?.name}
                            />
                        }
                        contentKey={displayedProperty?.id}
                        contentRef={animation.contentRef}
                        isAdvancing={animation.isAdvancing}
                        isSentLeadMessageVisible={sentLeadVendorName !== null}
                        shouldReduceMotion={animation.shouldReduceMotion}
                        vendorName={sentLeadVendorName ?? undefined}
                    >
                        {displayedProperty && (
                            <HomematchPropertyCardDetail
                                dictionaries={dictionaries}
                                map={<HomematchTinderPropertyMap property={displayedProperty} />}
                                onShowAll={() => trackHomematchTinderSeeAll(displayedProperty)}
                                onShowMore={() => trackHomematchTinderMoreInfo(displayedProperty)}
                                property={displayedProperty}
                            />
                        )}
                    </HomematchTinderDetailsCard>
                </HomematchTinderDeck>
            )}
        </HomematchTinderListingModal>
    );
};

const modalWidthStyle = css({
    md: {
        maxWidth: "97.8rem"
    }
});

const statePanelStyle = css({
    alignItems: "flex-start",
    backgroundColor: "var(--colors-white)",
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    boxShadow: "var(--shadow-2)",
    display: "flex",
    flex: "1 1 auto",
    flexDirection: "column",
    gap: 2,
    mx: 1.5,
    p: 3,

    md: {
        mx: 0
    }
});

const stateTextStyle = css({
    color: "var(--colors-secondary)",
    margin: 0
});
import {useEffect, useRef, useState} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Text} from "@pg-design/text-module";
import {
    getTinderDeckOfferCounterLabel,
    HomematchPropertyCardDetail,
    HomematchThumbDirection,
    HomematchTinderActionBar,
    HomematchTinderDeck,
    HomematchTinderDetailsCard,
    HomematchTinderListingModal,
    useHomematchTinderDeckAnimation
} from "@pg-rp/homematch-tinder";

import {addNotification} from "../../notifications/state/notifications_slice";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch} from "../../store/hooks";
import {useGetHomematchPropertyQuery} from "../api/get_homematch_property";
import {useGetHomematchVariablesQuery} from "../api/get_homematch_variables";
import {useHomematchTinderDeck} from "../hooks/use_homematch_tinder_deck";
import {
    trackHomematchTinderClose,
    trackHomematchTinderMoreInfo,
    trackHomematchTinderRate,
    trackHomematchTinderSeeAll,
    trackHomematchTinderViewItem
} from "../tracking/track_homematch_tinder";
import {HomematchTinderPropertyMap} from "./HomematchTinderPropertyMap";

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

const TINDER_MODAL_OVERLAY_Z_INDEX = 9000;
const SENT_LEAD_MESSAGE_VISIBILITY_MS = 3500;

export const HomematchTinderModal = (props: IProps) => {
    const dispatch = useAppDispatch();
    const deck = useHomematchTinderDeck({isOpen: props.isOpen});
    /** Cards swiped since the modal opened — drives the counter and the advance animation. */
    const [sessionSwipes, setSessionSwipes] = useState(0);
    const [sentLeadVendorName, setSentLeadVendorName] = useState<string | null>(null);
    const sentLeadTimerRef = useRef<number>(undefined);
    const viewedPreleadIdsRef = useRef(new Set<number>());

    useEffect(() => () => window.clearTimeout(sentLeadTimerRef.current), []);

    const currentPrelead = deck.currentPrelead;
    const propertyRpId = currentPrelead?.property.rp_id ?? null;
    const isDeckEmpty = !deck.isLoading && currentPrelead === null;

    const {
        currentData: property,
        isError: isPropertyError,
        isFetching,
        isLoading,
        isUninitialized
    } = useGetHomematchPropertyQuery({propertyRpId: propertyRpId ?? 0}, {skip: propertyRpId === null, refetchOnMountOrArgChange: true});
    const {data: variables} = useGetHomematchVariablesQuery(undefined, {skip: !props.isOpen});

    const isPropertyLoading = isLoading || isUninitialized || isFetching;

    const animation = useHomematchTinderDeckAnimation({
        activePreleadId: currentPrelead?.id,
        currentPreleadIndex: sessionSwipes,
        isOpen: props.isOpen
    });

    // The outgoing card keeps its content while the advance animation runs.
    const lastPropertyRef = useRef(property);

    useEffect(() => {
        if (!animation.isAdvancing) {
            lastPropertyRef.current = property;
        }
    }, [animation.isAdvancing, property]);

    const displayedProperty = animation.isAdvancing ? lastPropertyRef.current : property;
    const currentPreleadId = currentPrelead?.id;

    useEffect(() => {
        if (currentPreleadId === undefined) {
            return;
        }

        animation.handleCardReady(currentPreleadId, isPropertyError || (!isPropertyLoading && Boolean(property)));
    }, [animation.handleCardReady, currentPreleadId, isPropertyError, isPropertyLoading, property]);

    useEffect(() => {
        if (!props.isOpen) {
            viewedPreleadIdsRef.current.clear();
            setSessionSwipes(0);
        }
    }, [props.isOpen]);

    useEffect(() => {
        if (
            !props.isOpen ||
            animation.isAdvancing ||
            isPropertyLoading ||
            deck.leadRequirementsError ||
            !property ||
            property.id !== propertyRpId ||
            currentPreleadId === undefined ||
            viewedPreleadIdsRef.current.has(currentPreleadId)
        ) {
            return;
        }

        trackHomematchTinderViewItem(property, viewedPreleadIdsRef.current.size);
        viewedPreleadIdsRef.current.add(currentPreleadId);
    }, [animation.isAdvancing, currentPreleadId, deck.leadRequirementsError, isPropertyLoading, property, propertyRpId, props.isOpen]);

    const isRatingBlocked = !property || property.id !== propertyRpId || isPropertyLoading || deck.isSwiping || animation.isAdvancing;

    const acceptCurrent = async () => {
        const prelead = currentPrelead;

        if (!prelead || !property || isRatingBlocked) {
            return;
        }

        const result = await deck.acceptCurrentPrelead();

        if (result === "success") {
            trackHomematchTinderRate(property, "like");
            setSessionSwipes((swipes) => swipes + 1);
            setSentLeadVendorName(prelead.property.offer?.vendor?.name ?? null);
            window.clearTimeout(sentLeadTimerRef.current);
            sentLeadTimerRef.current = window.setTimeout(() => setSentLeadVendorName(null), SENT_LEAD_MESSAGE_VISIBILITY_MS);
        } else if (result === "error") {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
        }
    };

    const rejectCurrent = async () => {
        const prelead = currentPrelead;

        if (!prelead || !property || isRatingBlocked) {
            return;
        }

        const result = await deck.rejectCurrentPrelead({reasons: []});

        if (result === "success") {
            trackHomematchTinderRate(property, "dislike");
            setSessionSwipes((swipes) => swipes + 1);
        } else if (result === "error") {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
        }
    };

    const handleThumbClick = (direction: HomematchThumbDirection) => {
        if (direction === HomematchThumbDirection.Up) {
            acceptCurrent();
        } else {
            rejectCurrent();
        }
    };

    const isShellLoading = sessionSwipes === 0 && !property && (deck.isLoading || isPropertyLoading) && !isDeckEmpty;
    const showDeckShadows = deck.shadowPreleadIds.length > 0 || animation.isAdvancing;

    const offerCounterLabel = currentPrelead
        ? getTinderDeckOfferCounterLabel({
              preleadId: currentPrelead.id,
              remainingPreleadsList: deck.queue,
              trackingIndex: sessionSwipes
          })
        : undefined;

    const dictionaries = variables
        ? {
              facilitiesInfo: variables.offer_facilities,
              parkingPlacesTypes: variables.offer_parking_place_type,
              quarterTypes: variables.property_quarters,
              standardDescriptions: variables.offer_standard_description_type
          }
        : undefined;

    return (
        <HomematchTinderListingModal
            className={modalWidthStyle}
            handleClose={props.onClose}
            isLoading={isShellLoading}
            isOpen={props.isOpen}
            offerCounterLabel={offerCounterLabel}
            onCloseButtonClick={() => trackHomematchTinderClose("close_x")}
            onCloseOutsideClick={() => trackHomematchTinderClose("close_outside")}
            overlayZIndex={TINDER_MODAL_OVERLAY_Z_INDEX}
        >
            {deck.leadRequirementsError ? (
                <div className={statePanelStyle} role="alert">
                    <Text as="p" variant="body_copy_1" className={stateTextStyle}>
                        {deck.leadRequirementsError.detail}
                    </Text>
                    <Button variant="filled_primary" size="medium" onClick={deck.clearLeadRequirementsError}>
                        Wróć do przeglądania
                    </Button>
                </div>
            ) : isDeckEmpty ? (
                <div className={statePanelStyle} role="status">
                    <Text as="p" variant="body_copy_1" className={stateTextStyle}>
                        To wszystko na teraz — przygotowujemy kolejne dopasowania, wróć do nas później.
                    </Text>
                    <Button variant="filled_primary" size="medium" onClick={props.onClose}>
                        Zamknij
                    </Button>
                </div>
            ) : (
                <HomematchTinderDeck
                    frozenHeight={animation.frozenHeight}
                    isAdvancing={animation.isAdvancing}
                    setDeckNode={animation.setDeckNode}
                    shadowKeys={deck.shadowPreleadIds}
                    shouldReduceMotion={animation.shouldReduceMotion}
                    showDeckShadows={showDeckShadows}
                >
                    <HomematchTinderDetailsCard
                        actionBar={
                            <HomematchTinderActionBar
                                disabled={isRatingBlocked}
                                onThumbClick={handleThumbClick}
                                vendorLogoUrl={currentPrelead?.property.offer?.vendor?.logo_thumbnails?.v_log_100}
                                vendorName={currentPrelead?.property.offer?.vendor?.name}
                            />
                        }
                        contentKey={displayedProperty?.id}
                        contentRef={animation.contentRef}
                        isAdvancing={animation.isAdvancing}
                        isSentLeadMessageVisible={sentLeadVendorName !== null}
                        shouldReduceMotion={animation.shouldReduceMotion}
                        vendorName={sentLeadVendorName ?? undefined}
                    >
                        {displayedProperty && (
                            <HomematchPropertyCardDetail
                                dictionaries={dictionaries}
                                map={<HomematchTinderPropertyMap property={displayedProperty} />}
                                onShowAll={() => trackHomematchTinderSeeAll(displayedProperty)}
                                onShowMore={() => trackHomematchTinderMoreInfo(displayedProperty)}
                                property={displayedProperty}
                            />
                        )}
                    </HomematchTinderDetailsCard>
                </HomematchTinderDeck>
            )}
        </HomematchTinderListingModal>
    );
};

const modalWidthStyle = css({
    md: {
        maxWidth: "97.8rem"
    }
});

const statePanelStyle = css({
    alignItems: "flex-start",
    backgroundColor: "var(--colors-white)",
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    boxShadow: "var(--shadow-2)",
    display: "flex",
    flex: "1 1 auto",
    flexDirection: "column",
    gap: 2,
    mx: 1.5,
    p: 3,

    md: {
        mx: 0
    }
});

const stateTextStyle = css({
    color: "var(--colors-secondary)",
    margin: 0
});
